import { requestIdleCallback } from "@theme/utilities";

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

// Using WeakMap is crucial. It allows garbage collection of DOM elements
// when they are removed from the page, preventing memory leaks.
/** @type {WeakMap<Element, Map<string, Element | null>>} */
const elementCache = new WeakMap();
/** @type {WeakMap<Element, Map<string, { instance: Component<any> | null; callback: Function; data: any; }>>} */
const handlerCache = new WeakMap();

/**
 * Base class that powers our custom web components.
 *
 * Manages references to child elements with `ref` attributes and sets up mutation observers to keep
 * the refs updated when the DOM changes. Also handles declarative event listeners using.
 *
 * @template {Refs} [T=Refs]
 * @extends {DeclarativeShadowElement}
 */
export class Component extends DeclarativeShadowElement {
  /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * An array of required refs. If a ref is not found, an error will be thrown.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  /**
   * @private
   * @type {WeakMap<Element, string>}
   * A reverse map to quickly find the ref name for a given element.
   * This is essential for performant removal of refs.
   */
  #refMap = new WeakMap();

  /**
   * A flag to determine if the component's DOM is static.
   * If true, the MutationObserver will not be used.
   * @private
   */
  #isStatic = false;

  /**
   * Gets the root node of the component, which is either its shadow root or the component itself.
   *
   * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Initializes event listeners and refs.
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners(); // Safe to call on mount; only runs once.

    // Check for the 'static' attribute once on connection.
    this.#isStatic = this.hasAttribute("static");

    // Initial full scan is still necessary on connection.
    this.#updateAllRefs();

    // If the component is NOT static, set up the observer.
    if (!this.#isStatic) {
      requestIdleCallback(() => {
        // Double-check we haven't been disconnected while waiting for idle callback.
        if (!this.isConnected) return;

        for (const root of this.roots) {
          this.#mutationObserver.observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["ref"],
            attributeOldValue: true,
          });
        }
      });
    }
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    // takeRecords() clears pending mutations so they aren't processed twice.
    // If the observer is active, clear its queue before the manual update.
    if (!this.#isStatic) {
      this.#mutationObserver.takeRecords();
    }

    // When an entire section is re-rendered, we must do a full rescan.
    this.#updateAllRefs();
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Disconnects the mutation observer.
   */
  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  #updateAllRefs() {
    // Clear previous state
    this.refs = /** @type {RefsType<T>} */ ({});
    this.#refMap = new WeakMap();

    for (const root of this.roots) {
      for (const element of root.querySelectorAll("[ref]")) {
        this.#addRef(element);
      }
    }

    this.#validateRequiredRefs();
  }

  /**
   * Adds a single element to the refs object and reverse map.
   * @param {Node} node
   */
  #addRef(node) {
    if (!(node instanceof Element)) return;

    // Use the original, correct logic: Check the element's direct component parent.
    if (!this.#isDescendant(node)) {
      return;
    }

    const refName = node.getAttribute("ref") ?? "";
    if (!refName) return;

    this.#refMap.set(node, refName);

    const isArray = refName.endsWith("[]");
    const path = isArray ? refName.slice(0, -2) : refName;

    if (isArray) {
      const array = this.refs[path] || [];
      if (Array.isArray(array)) {
        array.push(node);
        this.refs[path] = array;
      }
    } else {
      this.refs[path] = node;
    }
  }

  /**
   * Removes a single element from the refs object using the reverse map.
   * @param {Node} node
   * @param {string | null} [forcedRefName] - The ref name from mutation.oldValue
   */
  #removeRef(node, forcedRefName = null) {
    if (!(node instanceof Element)) return;

    const refName = forcedRefName ?? this.#refMap.get(node);
    if (!refName) return; // If it's not in our map, we don't own it. This implicitly handles the descendant check.

    const isArray = refName.endsWith("[]");
    const path = isArray ? refName.slice(0, -2) : refName;

    if (isArray) {
      const array = this.refs[path];
      if (Array.isArray(array)) {
        const index = array.indexOf(node);
        if (index > -1) array.splice(index, 1);
      }
    } else if (this.refs[path] === node) {
      delete this.refs[path];
    }

    this.#refMap.delete(node);
  }

  /**
   * Checks if all required refs are present.
   */
  #validateRequiredRefs() {
    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in this.refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }
  }

  /**
   * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        mutation.addedNodes.forEach((node) => this.#addRef(node));
        mutation.removedNodes.forEach((node) => this.#removeRef(node));
      } else if (mutation.type === "attributes") {
        // Handle case where a 'ref' attribute is changed, added, or removed
        const oldRef = mutation.oldValue;
        const newRef = /** @type {Element} */ (mutation.target).getAttribute(
          "ref",
        );
        if (oldRef !== newRef) {
          // #removeRef will only act if it finds the node in its map.
          if (oldRef) this.#removeRef(mutation.target, oldRef);
          // #addRef will check if the target is a valid descendant.
          if (newRef) this.#addRef(mutation.target);
        }
      }
    }
  });

  /**
   * Checks if a given node is a descendant of this component.
   *
   * @param {Node} node - The node to check.
   * @returns {boolean} True if the node is a descendant of this component.
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;
}

/**
 * Get the ancestor of a given node.
 *
 * @param {Node} node - The node to get the ancestor of.
 * @returns {Node | null} The ancestor of the node or null if none is found.
 */
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;

  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;

  return null;
}

/**
 * Recursively finds the closest ancestor that is an instance of `Component`.
 *
 * @param {Node | null} node - The starting node to search from.
 * @returns {HTMLElement | null} The closest ancestor `Component` instance or null if none is found.
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (
    node instanceof HTMLElement &&
    node.tagName.toLowerCase().endsWith("-component")
  )
    return node;

  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);

  return null;
}

/**
 * Initializes the event listeners for custom event handling.
 *
 * Sets up event listeners for specified events and delegates the handling of those events
 * to methods defined on the closest `Component` instance, based on custom attributes.
 */
let initialized = false;

const events = [
  "click",
  "change",
  "select",
  "focus",
  "blur",
  "submit",
  "input",
  "keydown",
  "keyup",
  "toggle",
];
const shouldBubble = ["focus", "blur"];
const expensiveEvents = ["pointerenter", "pointerleave"];

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const handlerElement = getElement(event);
        if (!handlerElement) return;

        // Cache the parsed handler details
        let handlerMap = handlerCache.get(handlerElement);
        let handlerInfo;

        if (handlerMap) {
          handlerInfo = handlerMap.get(event.type);
        }

        if (!handlerInfo) {
          // This parsing logic only runs once per element
          const value = handlerElement.getAttribute(attribute) ?? "";
          let [selector, method] = value.split("/");
          const data = value.match(/(?<=[\/\?][^\/\?]+)[\/\?][^\/\?]+$/)?.[0];
          const instance = selector
            ? selector.startsWith("#")
              ? document.querySelector(selector)
              : handlerElement.closest(selector)
            : getClosestComponent(handlerElement);

          if (!(instance instanceof Component) || !method) return;

          method = method.replace(/\?.*/, "");
          const callback = /** @type {any} */ (instance)[method];

          if (typeof callback !== "function") return;

          handlerInfo = {
            instance,
            callback: callback.bind(instance),
            data: data ? parseData(data) : undefined,
          };

          if (!handlerMap) {
            handlerMap = new Map();
            handlerCache.set(handlerElement, handlerMap);
          }
          handlerMap.set(event.type, handlerInfo);
        }

        // This Proxy is lightweight and still useful for `event.target`
        const proxiedEvent =
          event.target !== handlerElement
            ? new Proxy(event, {
                get(originalEvent, property) {
                  // Intercept request for the `target` property
                  if (property === "target") {
                    return handlerElement;
                  }

                  // Get the value from the original event
                  const value = Reflect.get(originalEvent, property);

                  // If the value is a function, return it BOUND to the original event.
                  // This is the key to fixing "Illegal invocation".
                  if (typeof value === "function") {
                    return value.bind(originalEvent);
                  }

                  // Otherwise, return the value as is.
                  return value;
                },
              })
            : event;

        try {
          const args = [proxiedEvent];
          if (handlerInfo.data !== undefined) args.unshift(handlerInfo.data);
          handlerInfo.callback(...args);
        } catch (error) {
          console.error(
            `Error in event handler for "${attribute}" on`,
            handlerElement,
            error,
          );
        }
      },
      { capture: true },
    );
  }
}

/**
 * @param {Event} event
 */
function getElement(event) {
  const target = event.composedPath?.()[0] ?? event.target;
  if (!(target instanceof Element)) return null;

  // Cache the DOM traversal result
  const eventMap = elementCache.get(target);
  if (eventMap && eventMap.has(event.type)) {
    return eventMap.get(event.type) || null;
  }

  let result = null;
  if (target.hasAttribute(`on:${event.type}`)) {
    result = target;
  } else if (
    !expensiveEvents.includes(event.type) &&
    (event.bubbles || shouldBubble.includes(event.type))
  ) {
    result = target.closest(`[on\\:${event.type}]`);
  }

  // Ensure the inner map exists before setting a value on it
  if (!eventMap) {
    const newEventMap = new Map();
    newEventMap.set(event.type, result);
    elementCache.set(target, newEventMap);
  } else {
    eventMap.set(event.type, result);
  }

  return result;
}

/**
 * Parses a string to extract data based on a delimiter.
 *
 * @param {string} str - The string to parse.
 * @returns {Object|Array<string|number>|string} The parsed data.
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === "?"
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [
          key,
          parseValue(value),
        ]),
      )
    : parseValue(data);
}

/**
 * @typedef {Object|Array<string|number>|string} Data
 */

/**
 * Parses a string value to its appropriate type.
 *
 * @param {string} str - The string to parse.
 * @returns {Data} The parsed value.
 */
function parseValue(str) {
  if (str === "true") return true;
  if (str === "false") return false;

  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== "") return maybeNumber;

  return str;
}

/**
 * Throws a formatted error when a required ref is not found in the component.
 */
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(
      `Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`,
    );
  }
}
